import {
  sendSignal,
  startAuthentication,
  startRegistration,
  WebAuthnAbortService,
  WebAuthnError,
  type PublicKeyCredentialCreationOptionsJSON,
  type PublicKeyCredentialRequestOptionsJSON,
} from '@simplewebauthn/browser';
import axios, { AxiosError } from 'axios';

import { AUTH_BROKER_URL } from '@/constant/features';
import { AUTH_TOKEN_KEY } from '@/constant/localStorage';
import storage from '@/utils/storage';

/**
 * Client for the vh3-auth Worker (passkeys, FSI #1000). It has its own axios instance on purpose:
 * the shared one logs every response body to the console, and sign-in responses carry tokens.
 */
const broker = axios.create({
  baseURL: AUTH_BROKER_URL,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
  timeout: 20_000,
});

broker.interceptors.request.use(config => {
  const token = storage.getItem(AUTH_TOKEN_KEY);
  if (token && !/\/v1\/passkey\/login\//.test(config.url ?? '')) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/** Stable codes from the Worker (spec §4.1), plus the browser-side ones. */
export type PasskeyErrorCode =
  | 'cancelled'
  | 'already_registered'
  | 'not_supported'
  | 'reauth_required'
  | 'unauthorized'
  | 'account_unavailable'
  | 'ceremony_expired'
  | 'verification_failed'
  | 'passkey_not_recognised'
  | 'rate_limited'
  | 'not_found'
  | 'bad_request'
  | 'unavailable';

export class PasskeyError extends Error {
  constructor(
    readonly code: PasskeyErrorCode,
    readonly credentialId?: string
  ) {
    super(code);
    this.name = 'PasskeyError';
  }
}

const KNOWN: readonly string[] = [
  'reauth_required',
  'unauthorized',
  'account_unavailable',
  'ceremony_expired',
  'verification_failed',
  'passkey_not_recognised',
  'rate_limited',
  'not_found',
  'bad_request',
  'unavailable',
];

function toPasskeyError(error: unknown): PasskeyError {
  if (error instanceof PasskeyError) return error;
  if (error instanceof WebAuthnError) {
    if (error.code === 'ERROR_AUTHENTICATOR_PREVIOUSLY_REGISTERED')
      return new PasskeyError('already_registered');
    if (
      error.code === 'ERROR_CEREMONY_ABORTED' ||
      error.name === 'NotAllowedError'
    )
      return new PasskeyError('cancelled');
    return new PasskeyError('not_supported');
  }
  if (error instanceof Error && error.name === 'NotAllowedError') {
    return new PasskeyError('cancelled');
  }
  if (error instanceof AxiosError) {
    const body = error.response?.data as
      | { error?: string; credentialId?: string }
      | undefined;
    const code = body?.error ?? '';
    return new PasskeyError(
      (KNOWN.includes(code) ? code : 'unavailable') as PasskeyErrorCode,
      body?.credentialId
    );
  }
  return new PasskeyError('unavailable');
}

/** User-facing text for each code. Cancelled shows nothing. */
export function passkeyErrorMessage(code: PasskeyErrorCode): string | null {
  switch (code) {
    case 'cancelled':
      return null;
    case 'already_registered':
      return 'This device already has a passkey for your account.';
    case 'not_supported':
      return "This browser or device can't create a passkey.";
    case 'reauth_required':
      return 'Please confirm it’s you first.';
    case 'unauthorized':
      return 'Your session has expired. Please sign in again.';
    case 'account_unavailable':
      return 'Your account is not available. Contact your administrator.';
    case 'ceremony_expired':
      return 'That took too long. Please try again.';
    case 'rate_limited':
      return 'Too many attempts. Please wait a minute and try again.';
    case 'not_found':
      return 'That passkey no longer exists.';
    case 'verification_failed':
    case 'passkey_not_recognised':
      return "The passkey couldn't be verified. Please try again.";
    default:
      return 'Passkeys are unavailable right now. Please try again later.';
  }
}

export interface Passkey {
  id: string;
  credentialId: string;
  name: string;
  providerName: string;
  createdAt: number;
  lastUsedAt: number | null;
  backedUp: boolean;
}

async function call<T>(fn: () => Promise<{ data: T }>): Promise<T> {
  try {
    return (await fn()).data;
  } catch (error) {
    throw toPasskeyError(error);
  }
}

interface LoginOptions {
  ceremonyId: string;
  options: PublicKeyCredentialRequestOptionsJSON;
}

const fetchLoginOptions = () =>
  call(() => broker.post<LoginOptions>('/v1/passkey/login/options'));

/** Tell the browser to stop offering a passkey the server doesn't know (Chrome/Edge 132+, Safari 26+). */
async function forgetUnknownPasskey(rpID: string, credentialID?: string) {
  if (!credentialID) return;
  try {
    await sendSignal({ signalName: 'unknownCredential', rpID, credentialID });
  } catch {
    // Not supported in this browser (e.g. Firefox): nothing to do.
  }
}

export const passkeys = {
  list: () => call(() => broker.get<Passkey[]>('/v1/passkeys')),

  /** Rename one of the User's passkeys (1–64 characters, trimmed by the Worker). */
  rename: (id: string, name: string) =>
    call(() =>
      broker.patch<{ id: string; name: string }>(
        `/v1/passkeys/${encodeURIComponent(id)}`,
        { name }
      )
    ),

  /**
   * Remove one of the User's passkeys. Throws `reauth_required` when their sign-in is older than
   * 10 minutes. Afterwards tells this browser to stop offering it, where the Signals API exists.
   */
  remove: async (id: string) => {
    const removed = await call(() =>
      broker.delete<{ id: string; credentialId: string; rpId?: string }>(
        `/v1/passkeys/${encodeURIComponent(id)}`
      )
    );
    // Production passkeys belong to vh3.ai, not app.vh3.ai, so use the Worker's RP ID.
    if (removed.rpId) {
      await forgetUnknownPasskey(removed.rpId, removed.credentialId);
    }
    return removed;
  },

  /**
   * Create a passkey on this device and save it. Throws PasskeyError: `cancelled` when the User
   * closes the browser prompt, `reauth_required` when their sign-in is older than 10 minutes.
   */
  register: async (name?: string) => {
    const { ceremonyId, options } = await call(() =>
      broker.post<{
        ceremonyId: string;
        options: PublicKeyCredentialCreationOptionsJSON;
      }>('/v1/passkey/register/options')
    );
    let response;
    try {
      response = await startRegistration({ optionsJSON: options });
    } catch (error) {
      throw toPasskeyError(error);
    }
    return call(() =>
      broker.post<{ id: string; name: string; createdAt: number }>(
        '/v1/passkey/register/verify',
        { ceremonyId, response, name }
      )
    );
  },

  /**
   * Sign in with any passkey for this site and return the new authToken (same as auth/login).
   * `autofill` starts a conditional request that the browser offers from the email field; it waits
   * until the User picks a passkey, and is cancelled by any other passkey request or by abort().
   * Throws PasskeyError; `cancelled` covers a closed prompt and an aborted autofill request.
   */
  login: async ({ autofill = false }: { autofill?: boolean } = {}) => {
    const { ceremonyId, options } = await fetchLoginOptions();
    let response;
    try {
      response = await startAuthentication({
        optionsJSON: options,
        useBrowserAutofill: autofill,
      });
    } catch (error) {
      throw toPasskeyError(error);
    }
    try {
      const { authToken } = await call(() =>
        broker.post<{ authToken: string }>('/v1/passkey/login/verify', {
          ceremonyId,
          response,
        })
      );
      return authToken;
    } catch (error) {
      if (
        error instanceof PasskeyError &&
        error.code === 'passkey_not_recognised'
      ) {
        await forgetUnknownPasskey(
          options.rpId ?? window.location.hostname,
          error.credentialId
        );
      }
      throw error;
    }
  },

  /** Cancel any passkey request in progress (e.g. the autofill request before a password sign-in). */
  abort: () => WebAuthnAbortService.cancelCeremony(),
};
